
import { Tables } from "@/integrations/supabase/types";

export type PlayerStats = {
  totalNet: number;
  expenseNet: number;
  totalSpent: number;
  winRate: number;
  totalSessions: number;
  avgBuyIns: number;
  maxWin: number;
  maxLoss: number;
  currentStreak: number;
  streakType: 'win' | 'loss' | 'none';
  rank: string;
  rankColor: string;
};

export function calculatePlayerStats(sessions: { net: number, buyIns: number, buyInUnit?: number }[], expenseNet: number = 0): PlayerStats {
  const totalSessions = sessions.length;
  if (totalSessions === 0) {
    return {
      totalNet: expenseNet,
      expenseNet: expenseNet,
      totalSpent: 0,
      winRate: 0,
      totalSessions: 0,
      avgBuyIns: 0,
      maxWin: 0,
      maxLoss: 0,
      currentStreak: 0,
      streakType: 'none',
      rank: expenseNet >= 0 ? 'Gold' : 'Silver',
      rankColor: expenseNet >= 0 ? 'text-yellow-500' : 'text-slate-400',
    };
  }

  const scoreNet = Math.round(sessions.reduce((sum, s) => sum + s.net, 0) * 100) / 100;
  const totalNet = Math.round((scoreNet + expenseNet) * 100) / 100;
  const totalSpent = Math.round(sessions.reduce((sum, s) => sum + (s.buyIns * (s.buyInUnit ?? 20)), 0) * 100) / 100;
  const wins = sessions.filter(s => s.net > 0).length;
  const winRate = (wins / totalSessions) * 100;
  const avgBuyIns = sessions.reduce((sum, s) => sum + s.buyIns, 0) / totalSessions;
  const maxWin = Math.max(0, ...sessions.map(s => s.net));
  const maxLoss = Math.min(0, ...sessions.map(s => s.net));

  // Streak calculation (sessions are assumed to be sorted by date descending in PlayerProfile, 
  // but let's assume they are passed in order from newest to oldest)
  let currentStreak = 0;
  let streakType: 'win' | 'loss' | 'none' = 'none';

  if (sessions.length > 0) {
    const firstNet = sessions[0].net;
    if (firstNet > 0) {
      streakType = 'win';
      for (const s of sessions) {
        if (s.net > 0) currentStreak++;
        else break;
      }
    } else if (firstNet < 0) {
      streakType = 'loss';
      for (const s of sessions) {
        if (s.net < 0) currentStreak++;
        else break;
      }
    }
  }

  let rank = 'Bronze';
  let rankColor = 'text-orange-700';

  if (totalNet >= 500) {
    rank = 'Diamond';
    rankColor = 'text-blue-400';
  } else if (totalNet >= 200) {
    rank = 'Platinum';
    rankColor = 'text-cyan-600';
  } else if (totalNet >= 0) {
    rank = 'Gold';
    rankColor = 'text-yellow-500';
  } else if (totalNet >= -200) {
    rank = 'Silver';
    rankColor = 'text-slate-400';
  } else {
    rank = 'Bronze';
    rankColor = 'text-orange-700';
  }

  return {
    totalNet,
    expenseNet,
    totalSpent,
    winRate,
    totalSessions,
    avgBuyIns,
    maxWin,
    maxLoss,
    currentStreak,
    streakType,
    rank,
    rankColor,
  };
}

export function getRadarData(stats: PlayerStats) {
  return [
    { subject: 'Win Rate', A: stats.winRate, fullMark: 100 },
    { subject: 'Score', A: Math.min(100, (stats.totalNet / 500) * 100), fullMark: 100 },
    { subject: 'Aggression', A: Math.min(100, (stats.avgBuyIns / 4) * 100), fullMark: 100 },
    { subject: 'Longevity', A: Math.min(100, (stats.totalSessions / 10) * 100), fullMark: 100 },
    { subject: 'Consistency', A: streakToConsistency(stats), fullMark: 100 },
  ];
}

export function getConsistencyRadarData(
  sessions: { net: number; buyIns: number; buyInUnit?: number }[],
  totalGroupSessions: number = 20,
) {
  if (sessions.length === 0) {
    return [
      { subject: 'Avg Gain', A: 0, fullMark: 100 },
      { subject: 'Participation', A: 0, fullMark: 100 },
      { subject: 'Stability', A: 0, fullMark: 100 },
      { subject: 'Win Rate', A: 0, fullMark: 100 },
      { subject: 'Best Run', A: 0, fullMark: 100 },
    ];
  }

  const nets = sessions.map(s => s.net);
  const avgGain = nets.reduce((a, b) => a + b, 0) / nets.length;
  // Normalize: +200 = 100%, -200 = 0%
  const avgGainScore = Math.min(100, Math.max(0, ((avgGain + 200) / 400) * 100));

  // Participation rate: sessions played vs reference total
  const participationScore = Math.min(100, (sessions.length / totalGroupSessions) * 100);

  // Stability = inverse volatility (lower std dev = higher stability)
  const mean = avgGain;
  const variance = nets.reduce((sum, n) => sum + Math.pow(n - mean, 2), 0) / nets.length;
  const stdDev = Math.sqrt(variance);
  const stabilityScore = Math.min(100, Math.max(0, 100 - (stdDev / 3)));

  // Win rate
  const wins = nets.filter(n => n > 0).length;
  const winRate = (wins / nets.length) * 100;

  // Best run (max consecutive wins)
  let bestRun = 0, currentRun = 0;
  for (const n of nets) {
    if (n > 0) { currentRun++; bestRun = Math.max(bestRun, currentRun); }
    else currentRun = 0;
  }
  const bestRunScore = Math.min(100, (bestRun / 5) * 100);

  return [
    { subject: 'Avg Gain', A: Math.round(avgGainScore), fullMark: 100 },
    { subject: 'Participation', A: Math.round(participationScore), fullMark: 100 },
    { subject: 'Stability', A: Math.round(stabilityScore), fullMark: 100 },
    { subject: 'Win Rate', A: Math.round(winRate), fullMark: 100 },
    { subject: 'Best Run', A: Math.round(bestRunScore), fullMark: 100 },
  ];
}

function streakToConsistency(stats: PlayerStats) {
    if (stats.streakType === 'win') {
        return Math.min(100, (stats.currentStreak / 5) * 100);
    }
    return 0;
}
